import { WorkflowRole } from '@exam-management/common/types';
import { type WorkflowStep, useGetQuestionCreationTaskByIdQuery } from '@exam-management/graphql';
import {
  type QuestionCreationWorkflowStepCode,
  getWorkflowRoleForTask,
} from '@exam-management/task-management';
import { useAuth } from '@shared/auth';
import { Loading } from '@shared/components';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useMemo } from 'react';

import type { QuestionCreationTask } from '../../types';

import { mapTaskToQuestionCreationTask } from './mapper';

type QuestionCreationWorkflowStep = Pick<WorkflowStep, 'name'> & {
  code: QuestionCreationWorkflowStepCode;
};

export interface QuestionCreationTaskDetailsContextData {
  task: QuestionCreationTask;
  status: {
    name: string;
    code: QuestionCreationWorkflowStepCode;
  };
  roles: {
    isOwner: boolean;
    isAssignee: boolean;
  };
}

interface QuestionCreationTaskDetailsProviderProps extends PropsWithChildren {
  taskId: string;
}

export const QuestionCreationTaskDetailsContext = createContext<
  QuestionCreationTaskDetailsContextData | undefined
>(undefined);

export default function QuestionCreationTaskDetailsProvider(
  props: Readonly<QuestionCreationTaskDetailsProviderProps>,
) {
  const { taskId, children } = props;

  const { userProfile } = useAuth();
  const { data: questionCreationTaskData, loading: isGettingQuestionCreationTask } =
    useGetQuestionCreationTaskByIdQuery({
      variables: {
        getTaskByIdInput: {
          taskId: taskId,
        },
      },
    });

  const taskDetails = useMemo(
    () => mapTaskToQuestionCreationTask(questionCreationTaskData?.wflGetTaskById?.task),
    [questionCreationTaskData],
  );

  const [isOwner, isAssignee] = useMemo(() => {
    if (!taskDetails) return [false, false];
    const workflowRoles = getWorkflowRoleForTask({
      userId: userProfile?.id ?? '',
      assigneeId: taskDetails?.assignee.id ?? '',
      assignerId: taskDetails?.assignerId ?? '',
      ownerId: taskDetails?.ownerId ?? '',
    });

    return workflowRoles.reduce(
      (acc, role) => [
        acc[0] || role === WorkflowRole.TASK_OWNER,
        acc[1] || role === WorkflowRole.TASK_ASSIGNEE,
      ],
      [false, false],
    );
  }, [taskDetails, userProfile]);

  const contextValue = useMemo(() => {
    return {
      task: taskDetails,
      status: {
        name: taskDetails?.currentWorkflowStep.name,
        code: `${taskDetails?.currentWorkflowStep.code}`,
      } as QuestionCreationWorkflowStep,
      roles: { isOwner, isAssignee },
    };
  }, [taskDetails, isOwner, isAssignee]);

  if (isGettingQuestionCreationTask) {
    return <Loading sx={{ height: '100%' }} />;
  }

  return (
    <QuestionCreationTaskDetailsContext.Provider value={contextValue}>
      {children}
    </QuestionCreationTaskDetailsContext.Provider>
  );
}
